import type { ReactNode } from "react";
import { Inbox, LoaderCircle, TriangleAlert } from "lucide-react";
import { cn } from "../../lib/cn";
import { fr } from "../../i18n/fr";
import { Button } from "./Button";

/**
 * Skeleton / Loading + EmptyState + ErrorState — états transverses de chargement/vide/erreur.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-obsidian-100/60",
        className,
      )}
    >
      <div className="shimmer-overlay" />
    </div>
  );
}

/** Bloc de plusieurs lignes de skeleton. */
export function SkeletonLines({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-4" />
      ))}
    </div>
  );
}

/** Spinner centré simple. */
export function Loading({ label = fr.common.chargement, className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 py-12 text-parchment-200/60", className)}>
      <LoaderCircle className="h-8 w-8 animate-spin text-arcane-300" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

interface StateProps {
  title?: ReactNode;
  message?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, message, icon, action, className }: StateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 py-14 text-center", className)}>
      <div className="text-parchment-200/40">{icon ?? <Inbox className="h-10 w-10" />}</div>
      <p className="font-display text-lg text-parchment-100">{title ?? fr.common.aucuneDonnee}</p>
      {message && <p className="max-w-sm text-sm text-parchment-200/55">{message}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  title,
  message,
  onRetry,
  className,
}: {
  title?: ReactNode;
  message?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 py-14 text-center", className)}>
      <TriangleAlert className="h-10 w-10 text-ember-300/80" />
      <p className="font-display text-lg text-parchment-100">{title ?? fr.common.erreur}</p>
      {message && <p className="max-w-sm text-sm text-parchment-200/55">{message}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {fr.common.reessayer}
        </Button>
      )}
    </div>
  );
}
